import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, useApi } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader, Loading, Badge, Field, NativeSelect, ConfirmDialog } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ROLE_LABEL } from "../lib/format";

const ROLE_CLS = { admin: "bg-amber-950 border-amber-500 text-amber-200", responsabile: "bg-sky-950 border-sky-500 text-sky-200", operaio: "bg-slate-800 border-slate-500 text-slate-200" };
const EMPTY = { email: "", first_name: "", last_name: "", phone: "", role: "operaio", worker_id: "", active: true, password: "" };

const userInitial = (user) => (user ? { ...EMPTY, ...user, worker_id: user.worker_id || "", phone: user.phone || "", password: "" } : EMPTY);

function useUserForm(open, user, onDone) {
  const [f, setF] = useState(EMPTY);
  const [workers, setWorkers] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setF(userInitial(user));
    api.get("/workers").then((r) => setWorkers(r.data.items));
  }, [open, user]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...f, worker_id: f.worker_id ? Number(f.worker_id) : null, password: f.password || null };
      if (user) await api.put(`/users/${user.id}`, body);
      else await api.post("/users", body);
      toast.success(user ? "✓ Utente aggiornato" : "✓ Utente creato");
      onDone();
    } catch (err) {
      toast.error(`✕ ${errMsg(err)}`);
    } finally {
      setBusy(false);
    }
  };
  return { f, setF, set, workers, busy, submit };
}

function UserDialog({ open, onOpenChange, user, onSaved }) {
  const { f, setF, set, workers, busy, submit } = useUserForm(open, user, () => { onOpenChange(false); onSaved(); });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-popover max-w-lg" data-testid="user-form">
        <DialogHeader><DialogTitle>{user ? "Modifica utente" : "Nuovo utente"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-3">
          <Field label="Nome"><Input required value={f.first_name} onChange={set("first_name")} data-testid="user-first-name-input" /></Field>
          <Field label="Cognome"><Input required value={f.last_name} onChange={set("last_name")} data-testid="user-last-name-input" /></Field>
          <Field label="Email" className="sm:col-span-2"><Input type="email" required value={f.email} onChange={set("email")} data-testid="user-email-input" /></Field>
          <Field label="Telefono"><Input value={f.phone} onChange={set("phone")} /></Field>
          <Field label="Ruolo"><NativeSelect value={f.role} onChange={set("role")} data-testid="user-role-select">{Object.entries(ROLE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</NativeSelect></Field>
          {f.role === "operaio" && (
            <Field label="Scheda operaio collegata" className="sm:col-span-2"><NativeSelect value={f.worker_id} onChange={set("worker_id")} data-testid="user-worker-select">
              <option value="">— Nessuna —</option>{workers.map((w) => <option key={w.id} value={w.id}>{w.full_name}</option>)}
            </NativeSelect></Field>
          )}
          <Field label={user ? "Nuova password (opzionale)" : "Password (min 8 caratteri)"}><Input type="password" required={!user} value={f.password} onChange={set("password")} data-testid="user-password-input" /></Field>
          <label className="flex items-center gap-2 text-sm pt-6"><Switch checked={f.active} onCheckedChange={(v) => setF((p) => ({ ...p, active: v }))} data-testid="user-active-switch" />Attivo</label>
          <Button type="submit" disabled={busy} className="sm:col-span-2" data-testid="user-save-btn">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Salva</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function UsersPage() {
  const { user: me } = useAuth();
  const { data, loading, reload } = useApi("/users");
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);
  const [del, setDel] = useState(null);
  const remove = async () => {
    try {
      await api.delete(`/users/${del.id}`);
      toast.success("✓ Utente eliminato");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setDel(null);
  };
  return (
    <div>
      <PageHeader eyebrow="Amministrazione" title="Utenti" subtitle="Account e livelli di accesso"
        actions={<Button onClick={() => { setEdit(null); setOpen(true); }} data-testid="users-new-btn"><Plus className="h-4 w-4 mr-2" />Nuovo utente</Button>} />
      {loading && !data ? <Loading /> : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="users-list">
          {(data?.items || []).map((u) => (
            <div key={u.id} className="surface p-4 flex items-center gap-3" data-testid={`user-card-${u.id}`}>
              <div className="h-11 w-11 rounded-full bg-sky-500/15 text-sky-300 flex items-center justify-center font-bold text-sm">{u.first_name[0]}{u.last_name[0]}</div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{u.full_name} {!u.active && <span className="text-xs text-red-400">(disattivato)</span>}</div>
                <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                <Badge className={`${ROLE_CLS[u.role]} mt-1.5`}>{ROLE_LABEL[u.role]}</Badge>
              </div>
              <Button size="icon" variant="ghost" onClick={() => { setEdit(u); setOpen(true); }} data-testid={`user-edit-${u.id}`}><Pencil className="h-4 w-4" /></Button>
              {u.id !== me.id && <Button size="icon" variant="ghost" className="text-red-400" onClick={() => setDel(u)} data-testid={`user-delete-${u.id}`}><Trash2 className="h-4 w-4" /></Button>}
            </div>
          ))}
        </div>
      )}
      <UserDialog open={open} onOpenChange={setOpen} user={edit} onSaved={reload} />
      <ConfirmDialog open={!!del} onOpenChange={() => setDel(null)} title={`Eliminare ${del?.full_name}?`} onConfirm={remove} />
    </div>
  );
}
